import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, Flower, Gift, Truck, Cake, Heart, Sparkles, Handshake, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import Layout from '../layout/Layout';
import { mockReviews, mockFAQ, mockGalleryImages, occasions, businessInfo } from '../../data/mockData';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';

const occasionIcons = {
  cake: Cake,
  heart: Heart,
  sparkles: Sparkles,
  handshake: Handshake,
  gift: Gift,
};

const heroTrustHighlights = [
  { label: 'Freshly arranged', icon: Flower },
  { label: 'Gift note included', icon: Gift },
  { label: 'Premium wrapping', icon: Sparkles },
  { label: 'Fast WhatsApp response', icon: MessageCircle },
  { label: 'Delivery across LA (from $10)', icon: Truck },
];

const HomePage = () => {
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="flowberry-cinematic-hero relative min-h-[860px] overflow-hidden bg-gradient-to-b from-[#1a1a1d] via-[#1f1f23] to-[#1a1a1d] md:min-h-[880px] lg:min-h-[920px]">
        {!heroVideoFailed && (
          <video
            className="hero-cinema-video absolute inset-0 h-full w-full object-cover"
            src="/assets/hero/Jellyfish.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            onError={() => setHeroVideoFailed(true)}
          />
        )}

        {/* Static fallback if the video cannot load */}
        {heroVideoFailed && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1d] via-[#1f1f23] to-[#1a1a1d]" />
        )}

        <div className="flowberry-hero-wordmark" aria-hidden="true">
          FLOWBERRY
        </div>

        <div className="relative z-10 flex min-h-[680px] w-full items-center px-4 pb-40 pt-20 sm:px-6 md:min-h-[700px] md:pb-44 md:pt-28 lg:min-h-[760px] lg:px-8 lg:pb-48 lg:pt-36 xl:px-10">
          <div className="hero-content-glass hero-trust-strip hero-main-glass max-w-3xl overflow-hidden rounded-[1.65rem] border border-white/[0.10] bg-white/[0.045] p-6 text-white shadow-[0_18px_58px_rgba(0,0,0,0.18)] backdrop-blur-sm sm:p-8 lg:p-10">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-rose-200/25 bg-black/[0.12] px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-rose-50 shadow-lg shadow-black/20 backdrop-blur-[1px]">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-300 shadow-[0_0_18px_rgba(253,164,175,0.9)]" />
              Glendale-crafted luxury roses
            </div>
            <div className="relative">
              <div className="absolute -left-6 top-3 hidden h-24 w-1 rounded-full bg-gradient-to-b from-rose-300 via-pink-500 to-transparent shadow-[0_0_35px_rgba(244,63,94,0.7)] md:block" />
              <h1 className="mb-6 font-serif text-4xl font-semibold leading-tight text-white drop-shadow-[0_10px_35px_rgba(0,0,0,0.75)] md:text-5xl lg:text-6xl">
                <span className="block">Premium Roses</span>
                <span className="block">Delivery in</span>
                <span className="block bg-gradient-to-r from-rose-200 via-pink-300 to-rose-400 bg-clip-text text-transparent">
                  Los Angeles
                </span>
              </h1>
              <p className="mb-8 text-lg leading-relaxed text-stone-100/90 drop-shadow-[0_8px_24px_rgba(0,0,0,0.85)] md:text-xl">
                <span className="block">Build your bouquet in seconds</span>
                <span className="block">• Starting from $45</span>
                <span className="block">• Glendale-based delivery across LA</span>
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link to="/build">
                  <Button size="lg" className="w-full sm:w-auto bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 text-base px-8 py-6 shadow-[0_18px_55px_rgba(245,188,177,0.38)] border-0">
                    Build Your Bouquet
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href={getDefaultInquiryURL()} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/[0.25] bg-white/[0.08] text-stone-100 hover:bg-white/[0.14] hover:text-white text-base px-8 py-6 backdrop-blur-md">
                    Order on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative z-20 -mt-[60px] mx-4 mb-5 max-w-7xl sm:mx-6 sm:mb-8 sm:mt-10 lg:absolute lg:bottom-8 lg:left-8 lg:right-8 lg:mx-auto lg:mb-0 lg:mt-0">
          <div className="hero-trust-strip grid grid-cols-1 overflow-hidden rounded-[1.65rem] border border-white/[0.15] bg-white/[0.09] text-white shadow-[0_24px_80px_rgba(0,0,0,0.28)] backdrop-blur-md sm:grid-cols-2 lg:grid-cols-5">
              {heroTrustHighlights.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`flex items-center gap-4 px-5 py-4 sm:px-6 lg:py-5 ${
                      index > 0 ? 'border-t border-white/[0.12] sm:border-l sm:border-t-0' : ''
                    } ${index === 2 ? 'sm:border-t lg:border-t-0' : ''} ${index === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-white/[0.18] bg-white/[0.08] shadow-[0_0_28px_rgba(251,113,133,0.18)]">
                      <IconComponent className="h-5 w-5 text-rose-100" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/90">
                        {item.label}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        
        {/* Decorative gradient */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1/2 h-full bg-gradient-to-l from-rose-500/5 to-transparent -z-10 hidden lg:block" />
      </section>

      {/* How It Works */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#FFF8F5] via-[#F7F2EE] to-[#F7F2EE]">
        <div className="pointer-events-none absolute -left-24 top-12 h-72 w-72 rounded-full bg-rose-200/35 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-[#F5BCB1]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[rgb(251,113,133)] mb-4">
              How It Works
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Order your perfect bouquet in four simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: 1, title: 'Choose Roses', desc: 'Select from 10 premium rose varieties', icon: Flower },
              { step: 2, title: 'Size & Quantity', desc: 'Pick stem length and number of roses', icon: Sparkles },
              { step: 3, title: 'Wrap & Delivery', desc: 'Add premium wrap, choose pickup or delivery', icon: Gift },
              { step: 4, title: 'Send via WhatsApp', desc: 'We confirm your order personally', icon: Truck },
            ].map((item) => (
              <div
                key={item.step}
                className="group rounded-2xl border border-rose-200/70 bg-white/62 px-6 py-8 text-center shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-rose-300/80 hover:bg-white/76 hover:shadow-[0_24px_70px_rgba(120,72,54,0.15)]"
              >
                <div className="relative mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-white/80 bg-gradient-to-br from-white/88 to-rose-50/80 shadow-[0_14px_40px_rgba(251,113,133,0.18),inset_0_1px_0_rgba(255,255,255,0.75)] transition-all duration-300 group-hover:shadow-[0_18px_48px_rgba(251,113,133,0.26),inset_0_1px_0_rgba(255,255,255,0.82)]">
                  <item.icon className="w-7 h-7 text-[#FB7185]" />
                  <span className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#F5BCB1] text-xs font-semibold text-stone-900 shadow-[0_10px_24px_rgba(245,188,177,0.42)] ring-4 ring-[#F7F2EE]">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-serif text-lg font-semibold text-stone-900 mb-2">{item.title}</h3>
                <p className="text-sm leading-relaxed text-stone-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/build">
              <Button className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0 px-7 shadow-[0_18px_45px_rgba(245,188,177,0.35)] hover:shadow-[0_22px_55px_rgba(245,188,177,0.46)]">
                Start Building
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="bg-[#F7F2EE]">
        <div className="section-divider-rose" aria-hidden="true" />
      </div>

      {/* Gallery Section */}
      <section className="py-20 md:py-28 bg-[#F7F2EE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="our-work-title font-serif text-3xl md:text-4xl font-semibold text-[rgb(251,113,133)] mb-4">
              Our Work
            </h2>
            <p className="text-stone-400 max-w-2xl mx-auto">
              Handcrafted arrangements for every occasion
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {mockGalleryImages.map((image) => (
              <div 
                key={image.id} 
                className="aspect-[4/5] rounded-2xl overflow-hidden bg-stone-800 relative group shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
              >
                <img
                  src={image.imagePath}
                  alt={image.alt}
                  width="900"
                  height="1125"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/18 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,rgba(255,244,238,0.22),rgba(251,113,133,0.08)_34%,transparent_72%)]" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 md:p-6">
                  <h3 className="max-w-[calc(100%-4rem)] font-serif text-2xl font-semibold text-white drop-shadow-[0_8px_22px_rgba(0,0,0,0.72)] [text-shadow:0_0_18px_rgba(255,244,238,0.34),0_8px_24px_rgba(0,0,0,0.7)]">
                    {image.title}
                  </h3>
                  <Link
                    to="/build"
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 text-white shadow-[0_12px_28px_rgba(0,0,0,0.28),0_0_28px_rgba(255,244,238,0.34)] backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:bg-white/30 hover:shadow-[0_14px_34px_rgba(0,0,0,0.3),0_0_42px_rgba(245,188,177,0.52)]"
                    aria-label={`Build ${image.title}`}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a 
              href={businessInfo.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-rose-400 hover:text-rose-300 font-medium transition-colors"
            >
              <Instagram className="w-5 h-5" />
              See more on Instagram
            </a>
          </div>
        </div>
      </section>

      <div className="bg-[#F7F2EE]">
        <div className="section-divider-rose" aria-hidden="true" />
      </div>

      {/* Occasions */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#F7F2EE] via-[#FFF8F5] to-[#F7F2EE]">
        <div className="pointer-events-none absolute left-1/2 top-10 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-rose-200/25 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-full w-[70%] min-w-[20rem] sm:w-[56%] md:w-[44%] md:min-w-[24rem]">
          <div className="absolute bottom-10 right-0 h-[65%] w-full rounded-full bg-[#FB7185]/12 blur-3xl lg:-right-24" />
          <img
            src="/images/occasion-floral-decor.png"
            alt=""
            aria-hidden="true"
            width="900"
            height="900"
            loading="lazy"
            className="absolute bottom-0 right-[-2rem] w-[34rem] max-w-none object-contain drop-shadow-[0_34px_80px_rgba(120,72,54,0.20)] sm:right-[-1rem] md:right-[2rem] md:w-[38rem] lg:right-[-2rem] lg:w-[52rem]"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative z-10">
            <div className="mb-12 text-center">
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#FB7185] mb-4">
                Perfect for Every Occasion
              </h2>
              <p className="text-stone-600 max-w-2xl mx-auto">
                Express your feelings with premium roses
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-5">
              {occasions.map((occasion) => {
                const IconComponent = occasionIcons[occasion.icon] || Gift;
                return (
                  <Link 
                    key={occasion.id} 
                    to="/build"
                    className="group"
                  >
                    <Card className="h-full rounded-lg border border-rose-200/70 bg-white/68 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl transition-all duration-300 hover:border-[#FB7185]/50 hover:bg-white/82 hover:shadow-lg hover:shadow-[#FB7185]/15">
                      <CardContent className="p-6 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl border border-[#FB7185]/25 bg-[#FB7185]/12 shadow-[0_10px_30px_rgba(251,113,133,0.18)] transition-colors mb-3 group-hover:bg-[#FB7185]/18 group-hover:border-[#FB7185]/40">
                          <IconComponent className="w-6 h-6 text-[#FB7185]" />
                        </div>
                        <h3 className="font-medium text-[#3A2A2A]">{occasion.name}</h3>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#F7F2EE] via-[#FFF8F5] to-[#F7F2EE]">
        <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-rose-200/35 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-[#F5BCB1]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#FB7185] mb-4">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockReviews.map((review) => (
              <Card key={review.id} className="rounded-lg border border-rose-200/70 bg-white/68 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl transition-all duration-300 hover:border-[#FB7185]/50 hover:bg-white/82 hover:shadow-lg hover:shadow-[#FB7185]/15">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FB7185] text-[#FB7185]" />
                    ))}
                  </div>
                  <p className="text-stone-700 text-sm mb-4 leading-relaxed">
                    "{review.text}"
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#3A2A2A]">{review.name}</span>
                    <span className="text-xs text-stone-500">{review.date}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <div className="bg-[#F7F2EE]">
        <div className="section-divider-rose" aria-hidden="true" />
      </div>

      {/* FAQ */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#F7F2EE] via-[#FFF8F5] to-[#F7F2EE]">
        <div className="pointer-events-none absolute left-1/2 top-10 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-rose-200/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-12 h-72 w-72 rounded-full bg-[#F5BCB1]/24 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#FB7185] mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {mockFAQ.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="rounded-lg border border-rose-200/70 bg-white/68 px-6 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl transition-all duration-300 hover:border-[#FB7185]/50 hover:bg-white/82 hover:shadow-lg hover:shadow-[#FB7185]/15">
                <AccordionTrigger className="text-left font-medium text-[#3A2A2A] hover:text-[#FB7185] hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <div className="bg-[#F7F2EE]">
        <div className="section-divider-rose" aria-hidden="true" />
      </div>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#F7F2EE] via-[#FFF8F5] to-[#F7F2EE]">
        <div className="pointer-events-none absolute left-1/2 top-8 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-[#F5BCB1]/30 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-8 rounded-[1.65rem] border border-rose-200/70 bg-white/68 px-6 py-8 text-center shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl sm:px-8 md:py-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:gap-10 lg:p-10 lg:text-left">
            <div className="relative overflow-hidden rounded-[1.35rem] border border-rose-200/70 bg-white/54 shadow-[0_22px_60px_rgba(120,72,54,0.16),0_0_50px_rgba(251,113,133,0.16)]">
              <video
                className="block aspect-[4/3] w-full object-cover"
                src="/images/Floral_animation.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
            </div>
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#FB7185] mb-4">
                Ready to Make Someone's Day?
              </h2>
              <p className="text-stone-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                Create a beautiful bouquet in under a minute. Starting from just $45.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/build">
                  <Button size="lg" className="w-full sm:w-auto bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 text-base px-8 py-6 border-0 shadow-[0_18px_55px_rgba(245,188,177,0.38)]">
                    Build Your Bouquet
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href={getDefaultInquiryURL()} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-[#FB7185]/30 bg-white/62 text-[#3A2A2A] hover:bg-white/82 hover:text-[#FB7185] text-base px-8 py-6 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
